import {
    AJAX_END,
    AJAX_ERROR,
    AJAX_START,
    CANCEL_AJAX_REQUEST,
    CANCEL_AJAX_REQUESTS,
    CLEAR_AJAX_ERROR,
    CLEAR_AJAX_ERROR_SUBPANELS,
    CLEAR_GLOBAL_MASK,
    SET_GLOBAL_MASK
} from '../constants';
import {StartRequestAction} from '~/frontend/src/actions/ajax/startRequest';
import {EndRequestAction} from '~/frontend/src/actions/ajax/endRequest';
import {AjaxErrorAction} from '~/frontend/src/actions/ajax/ajaxError';
import {ClearAjaxErrorAction} from '~/frontend/src/actions/ajax/clearAjaxError';
import {ClearAjaxErrorForSubpanelsAction} from '~/frontend/src/actions/ajax/clearAjaxErrorForSubpanels';
import {CancelRequestAction} from '~/frontend/src/actions/ajax/cancelRequest';
import {CancelRequestsAction} from '~/frontend/src/actions/ajax/cancelRequests';
import {SetGlobalMaskAction} from '~/frontend/src/actions/ajax/setGlobalMask';
import {ClearGlobalMaskAction} from '~/frontend/src/actions/ajax/clearGlobalMask';
import {ToJsonResult as ErrorToJsonResult} from '~/shared/src/utilities/error';
import {ToJsonResult as LocalizableErrorToJsonResult} from '~/shared/src/base/LocalizableError';
import {ToJsonResult as StackErrorToJsonResult} from '~/shared/src/base/StackError';

const relevantActions = [
    AJAX_START,
    AJAX_END,
    AJAX_ERROR,
    CLEAR_AJAX_ERROR,
    CLEAR_AJAX_ERROR_SUBPANELS,
    CANCEL_AJAX_REQUEST,
    CANCEL_AJAX_REQUESTS,
    SET_GLOBAL_MASK,
    CLEAR_GLOBAL_MASK
];

type RunningRequests = Map<string, Map<string, Function>>;
const runningRequests : RunningRequests = new Map();

type XhrActions = StartRequestAction | EndRequestAction | AjaxErrorAction | ClearAjaxErrorAction | ClearAjaxErrorForSubpanelsAction
    | CancelRequestAction | CancelRequestsAction | SetGlobalMaskAction | ClearGlobalMaskAction;

export type XhrComponentState = {
    [ requestUid : string ] : number
};
export type XhrError = {
    title : string;
    message? : string;
    code? : number;
    errors? : ( string | ErrorToJsonResult | LocalizableErrorToJsonResult | StackErrorToJsonResult )[];
    name? : never;
    type? : never;
    number? : never;
} | ( ( ErrorToJsonResult | LocalizableErrorToJsonResult | StackErrorToJsonResult ) & { title? : never; code? : never; } );
export type XhrState = {
    [ componentUid : string ] : XhrComponentState
} & {
    errors? : {
        [ componentUid : string ] : XhrError;
    }
};

/**
 * Helper function to increment request counter for given component and request uid
 * @param xhr
 * @param componentUid
 * @param requestUid
 */
function incrementRequestCounter( xhr : XhrState, componentUid : string, requestUid : string ) {
    if ( !xhr[ componentUid ] ) {
        xhr[ componentUid ] = {};
    }
    else {
        xhr[ componentUid ] = Object.assign( {}, xhr[ componentUid ] );
    }

    xhr[ componentUid ][ requestUid ] = xhr[ componentUid ][ requestUid ]
        ? xhr[ componentUid ][ requestUid ] + 1
        : 1;
}

/**
 * Helper function to decrement request counter for given component and request uid
 * @param xhr
 * @param componentUid
 * @param requestUid
 */
function decrementRequestCounter( xhr : XhrState, componentUid : string, requestUid : string ) {
    // decrement request counter to handle loading masks
    if ( xhr[ componentUid ]?.[ requestUid ] ) {
        xhr[ componentUid ] = Object.assign( {}, xhr[ componentUid ] );

        xhr[ componentUid ][ requestUid ]--;

        if ( xhr[ componentUid ][ requestUid ] < 1 ) {
            delete xhr[ componentUid ][ requestUid ];
        }
        if ( Object.keys( xhr[ componentUid ] ).length < 1 ) {
            delete xhr[ componentUid ];
        }
    }
}

/**
 *
 * @param xhr
 * @param action
 */
export default function xhrReducer( xhr : XhrState = {}, action : XhrActions ) : XhrState {

    if ( relevantActions.includes( action.type ) ) {
        xhr = Object.assign( {}, xhr );

        const componentUid = action.type !== SET_GLOBAL_MASK && action.type !== CLEAR_GLOBAL_MASK
                  ? action.componentUid ?? 'global'
                  : '',
              requestUid   = action.type !== CANCEL_AJAX_REQUESTS && action.type !== CLEAR_AJAX_ERROR && action.type !== CLEAR_AJAX_ERROR_SUBPANELS && action.type !== SET_GLOBAL_MASK && action.type !== CLEAR_GLOBAL_MASK
                  ? action.requestUid ?? 'default'
                  : '';

        switch ( action.type ) {
            case AJAX_START:
                incrementRequestCounter( xhr, componentUid, requestUid );

                // put cancel function for request to map
                if ( !runningRequests.has( componentUid ) ) {
                    runningRequests.set( componentUid, new Map() );
                }
                runningRequests.get( componentUid )?.set( requestUid, action.cancelFn );
                break;

            case AJAX_END:
                decrementRequestCounter( xhr, componentUid, requestUid );

                // remove cancel function for request from map
                if ( runningRequests.has( componentUid ) && runningRequests.get( componentUid )?.has( requestUid ) ) {
                    runningRequests.get( componentUid )?.delete( requestUid );
                    if ( !runningRequests.get( componentUid )?.size ) {
                        runningRequests.delete( componentUid );
                    }
                }
                break;

            case AJAX_ERROR:
                if ( xhr.errors ) {
                    xhr.errors = Object.assign( {}, xhr.errors );
                }
                else {
                    xhr.errors = {};
                }

                xhr.errors[ componentUid ] = {
                    title  : action.title,
                    message: action.message,
                    code   : action.code,
                    errors : action.errors
                }
                break;

            case CLEAR_AJAX_ERROR:
                if ( xhr?.errors?.[ componentUid ] ) {
                    xhr.errors = Object.assign( {}, xhr.errors );

                    delete xhr.errors[ componentUid ];
                }
                break;

            case CLEAR_AJAX_ERROR_SUBPANELS:
                if ( xhr?.errors ) {
                    const xhrErrorsKeys = Object.keys( xhr.errors ) as string[];

                    for ( const xhrErrorsKey of xhrErrorsKeys ) {
                        if ( xhrErrorsKey.match( `^${componentUid}[_-](.*)$` ) ) {
                            delete xhr.errors[ xhrErrorsKey ];
                        }
                    }
                }
                break;

            case CANCEL_AJAX_REQUEST:
                // cancel and remove request
                if ( runningRequests.has( componentUid ) && runningRequests.get( componentUid )?.has( requestUid ) ) {
                    decrementRequestCounter( xhr, componentUid, requestUid );

                    runningRequests.get( componentUid )?.get( requestUid )?.();

                    runningRequests.get( componentUid )?.delete( requestUid );
                    if ( !runningRequests.get( componentUid )?.size ) {
                        runningRequests.delete( componentUid );
                    }
                }
                break;

            case CANCEL_AJAX_REQUESTS:
                // iterate through all pending requests and cancel and remove them
                if ( runningRequests.has( componentUid ) ) {
                    runningRequests.get( componentUid )!.forEach( ( cancelFn, reqUid ) => {
                        decrementRequestCounter( xhr, componentUid, reqUid );

                        cancelFn();
                    } );

                    runningRequests.delete( componentUid );
                }
                break;

            case SET_GLOBAL_MASK:
                incrementRequestCounter( xhr, 'global', action.name );

                break;

            case CLEAR_GLOBAL_MASK:
                decrementRequestCounter( xhr, 'global', action.name );

                break;
        }
    }
    return xhr;
}
